import { TABLE_TYPE_SITNGO, TABLE_TYPE_TOURNAMENT } from 'constants/gameConstants';
import { useFeatureFlag } from 'featureFlags';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useExitToNextTable } from 'hooks/useExitToNextTable';
import { useTableId, useTableSelector } from 'hooks/useTableSelector';
import { clearSitGoResult } from 'reducers/sitGoResult';
import { removeWaitlistEntry } from 'reducers/waitlist';
import { selectIsQueuedFor } from 'reducers/waitlist.selectors';
import useGameService from 'services/GameService';
import { leaveWaitlist } from 'services/waitlistService';
import type { WaitlistGameType } from 'types/waitlist';
import { isWaitlistGameType } from 'types/waitlist';
import { markPlayerExitedTable } from 'utils/recentlyExitedTables';
import { markRecentlyLeftWaitlist } from 'utils/recentlyLeftWaitlists';

import { landOnSitGoTab } from 'components/SitGoResult/sitGoLobbyLanding';

// A registered player who is still IN a tournament shouldn't get an Exit Table
// affordance — leaving is a forfeit handled through the TournamentScreenModal.
// The button DOES appear for tournament tables when:
//   - the player is an OBSERVER (not registered in this tournament), or
//   - the player's tournament status is 'F' (busted / finished, or the
//     tournament itself has ended).
// Cash tables always show it. Same rule in single-table and multi-table view.
export const shouldHideExitTableButton = (
  tableType: string | undefined,
  isRegistered: boolean,
  isBustedOrFinished: boolean
): boolean => {
  const isTournament = tableType === TABLE_TYPE_TOURNAMENT || tableType === TABLE_TYPE_SITNGO;
  if (!isTournament) return false;
  // Observer (not registered) or out of the tournament → allow exit.
  if (!isRegistered || isBustedOrFinished) return false;
  // Registered and still in → hide (forfeit goes through TournamentScreenModal).
  return true;
};

/**
 * Whether the player's gotTnmStatus record (or the table itself) says they are
 * out of the tournament — the gate that shows the felt's Exit Table to a
 * registered player. Exported so a modal that relies on that button being
 * there (TournamentScreenModal's closed-table prompt) reads the same signal.
 */
export const isOutOfTournament = (
  record: { status?: string; tStatus?: string } | null | undefined,
  gameStatus: string | undefined
): boolean => record?.status === 'F' || record?.tStatus === 'F' || gameStatus === 'F';

/** Modal dismissal and felt navigation must use the same authenticated identity. */
export const findPlayerTournamentRecord = (
  records: unknown,
  playerGuid: string | null | undefined,
  tournamentId: unknown
):
  | { playerId?: string; tournamentId?: string | number; status?: string; tStatus?: string }
  | undefined => {
  if (!playerGuid || !Array.isArray(records)) return undefined;
  return records.find(
    (record) =>
      record?.playerId === playerGuid && Number(record?.tournamentId) === Number(tournamentId)
  );
};

/**
 * Encapsulates the "leave this table" action so both the header/footer
 * ExitTableButton and the account/table menu panel share ONE source of truth:
 * leave the seat, drop any waitlist entry, and hop to the next open table (or
 * the lobby). Also exposes whether the exit affordance should be hidden (a
 * registered, still-in tournament player) and the correct label.
 */
export const useLeaveTable = () => {
  const game = useTableSelector((table) => table.game);
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const isSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  const tableType = useTableSelector((table) => table.game.tabletype);
  const playerStatus = game[`p${seatId}status`];

  const guid = useAppSelector((state) => state.user.playerGUID);
  const tournamentStatus = useTableSelector((table) => table.tournamentStatus);
  const myTournamentRecord = findPlayerTournamentRecord(tournamentStatus, guid, game?.tournamentId);
  const isRegistered = !!myTournamentRecord || isSeated;
  const isBustedOrFinished = isOutOfTournament(myTournamentRecord, game?.status);

  const { leaveTable } = useGameService();
  const exitToNextTable = useExitToNextTable();
  const gameID = useTableId();
  // The tournament whose seat the player still holds at this table, if any
  // (their details have them playing here). Read from the details rather than
  // the table's slice, which may never have loaded — keyed by the panel's own id (the load-error modal's
  // Exit Table).
  const heldSeatIn = useAppSelector((state) => {
    const details = (state as any).lobby?.playerTournamentDetails;
    const here = Array.isArray(details)
      ? details.find((d: any) => d?.status === 'P' && String(d?.gameID) === String(gameID))
      : undefined;
    return here ? Number(here.tournamentId) : undefined;
  });
  const dispatch = useAppDispatch();
  const playerGuid = useAppSelector((state) => state.user.playerGUID) || '';
  const numericGameId = Number(gameID);
  const isQueued = useAppSelector(selectIsQueuedFor(numericGameId));
  const normalizedGameType: WaitlistGameType | null = isWaitlistGameType(game.gameType)
    ? game.gameType
    : isWaitlistGameType(game.game)
    ? game.game
    : null;

  // EN-11109 (release.sit-go-result-modal): leaving a finished or busted Sit &
  // Go withdraws its rematch offer and, when it was the last table, lands on
  // the lobby's Sit & Go tab with the format just played. Off: unchanged.
  const sitGoResultModal = useFeatureFlag('sitGoResultModal');
  const sitGoEntryCapacity = useAppSelector(
    (state) => (state as any).sitGoResult?.byGameId?.[String(gameID)]?.outcome?.capacity
  );
  const landsOnSitGoTab = sitGoResultModal && tableType === TABLE_TYPE_SITNGO && isBustedOrFinished;
  const sitGoCapacity =
    Number(sitGoEntryCapacity) ||
    Number((myTournamentRecord as any)?.maxSeat) ||
    Number(game?.maxSeat) ||
    0;

  const isHidden = shouldHideExitTableButton(tableType, isRegistered, isBustedOrFinished);
  // A queued-but-unseated player is leaving the waitlist, not a seat.
  const isWaitlistOnly = isQueued && !isSeated;
  const exitLabel = isWaitlistOnly ? 'Exit Waitlist' : 'Exit Table';

  /**
   * @param opts.skipWaitlistExit the caller has ALREADY left the waitlist (the
   *   Leaving-the-Waitlist modal does its own DELETE before calling this), so
   *   don't fire a second one. Everything else still runs: the engine leave and
   *   the table teardown are what remove the table from the navigation strip,
   *   and skipping them is what left a departed table sitting in the strip.
   */
  const leave = (opts?: { skipWaitlistExit?: boolean }) => {
    // Kept out of the nav while the engine still seats the player (e.g. a
    // deferred leave) until they reopen it — but only once the leave was sent;
    // an unsent one leaves them seated, and hiding the table would strand them.
    if (
      leaveTable(
        true,
        +playerStatus === 4 || +playerStatus === 5,
        Boolean(!isWaitlistOnly && gameID && playerGuid)
      )
    ) {
      // A Sit & Go the player is out of: the engine dropping its table from
      // GOT_PLAYER_GAMES and listing it again must not undo the exit.
      markPlayerExitedTable(gameID, {
        heldSeatIn,
        outOfTournament: tableType === TABLE_TYPE_SITNGO && isBustedOrFinished
      });
    }

    // Exit the waitlist too, otherwise the engine keeps the player queued and
    // the next broadcast re-adds the "WAIT · N" entry (WL-006B).
    if (!opts?.skipWaitlistExit && isQueued && !isSeated && normalizedGameType && playerGuid) {
      dispatch(removeWaitlistEntry({ gameId: numericGameId, playerGuid, myGuid: playerGuid }));
      markRecentlyLeftWaitlist(numericGameId);
      leaveWaitlist({ gameId: numericGameId, gameType: normalizedGameType, playerGuid }).catch(
        (err) => console.error('[waitlist] leaveWaitlist on table exit failed', err)
      );
    }

    // Remove this table and hop to the next open one (or the lobby if it was
    // the last) — shared with the Buy Back In modal's Exit Table.
    if (landsOnSitGoTab) {
      dispatch(clearSitGoResult(String(gameID)));
      exitToNextTable(gameID, { onReturnToLobby: () => landOnSitGoTab(sitGoCapacity) });
      return;
    }
    exitToNextTable(gameID);
  };

  return { leave, isHidden, isWaitlistOnly, exitLabel };
};
